import { Assets } from 'chessboard/plugins/assets/config/types';
import { Core } from 'chessboard/core/config/types';
import type { Plugins } from 'chessboard/core/plugins/config/types';
import { setBoardFallback } from 'plugins/assets/helpers/set-board-fallback';
import { setPiecesFallback } from 'plugins/assets/helpers/set-pieces-fallback';
import { verifyBoardAssetExistence } from 'plugins/assets/helpers/verify-board-asset-existence';
import { verifyPiecesAssetsExistence } from 'plugins/assets/helpers/verify-pieces-assets-existence';
import { isOneOf } from 'src/utils/app/model/is-one-of';
import { Board } from 'chessboard/model/board/types';
import { createCoreEvent } from 'chessboard/core/model/create-core-event';
import type { ThemeAssets } from 'chessboard/config/theme-assets';
import deepEqual from 'fast-deep-equal';
/**
 * Adds commands for updating assets in response to options changes.
 */
export function createAssetsPlugin(): Plugins.Model {
  let currentLegacyBoardStyle: string;
  let currentLegacyPieceStyle: string;
  let currentThemeAssets: ThemeAssets.Assets | undefined;

  return {
    match: [
      {
        condition: isOneOf([
          Core.InstanceEvents.RendererSet,
          Core.OptionsEvents.Update,
          Core.InstanceEvents.WebGLAssetsInitialized,
        ]),
        handler: updateAssets,
      },
    ],
    create: ({ options }: Core.Context) => {
      currentLegacyBoardStyle = options.boardStyle;
      currentLegacyPieceStyle = options.pieceStyle;
      currentThemeAssets = options.themeAssets;
    },
    name: Assets.Plugin.Name,
  };

  /**
   * Update the board and piece assets.
   */
  function updateAssets(_event: Core.Event, context: Core.Context) {
    const { options, renderer } = context;

    if (!renderer || !renderer.areAssetsLoaded()) {
      return;
    }

    const piecesChanged =
      options.pieceStyle !== currentLegacyPieceStyle ||
      !deepEqual(currentThemeAssets?.pieces, options.themeAssets?.pieces);

    const boardChanged =
      options.boardStyle !== currentLegacyBoardStyle ||
      !deepEqual(currentThemeAssets?.board, options.themeAssets?.board);

    const updateBoardStyles = () => {
      if (boardChanged) {
        renderer.updateBoardImage(context);
      }
    };

    if (context.options.themeAssets) {
      verifyBoardAssetExistence(context.options.themeAssets!)
        .then(updateBoardStyles)
        .catch((err) => {
          if (err instanceof Board.StylesSetFailed) {
            emitError(context, err);
            setBoardFallback(context);
          } else if (boardChanged) {
            updateBoardStyles();
          }
        });
    } else {
      updateBoardStyles();
    }

    const updatePieceStyles = () => {
      if (piecesChanged) {
        renderer.updatePieceBaseImage(context);
      }
    };

    if (renderer.name === 'webgl-3d' || !renderer.getPieceStyles) {
      updatePieceStyles();
    } else {
      const pieceStyles = renderer.getPieceStyles();

      verifyPiecesAssetsExistence(context.options, pieceStyles[context.options.pieceStyle])
        .then(updatePieceStyles)
        .catch((err) => {
          if (err instanceof Board.StylesSetFailed) {
            emitError(context, err);
            setPiecesFallback(context);
          } else if (piecesChanged) {
            updatePieceStyles();
          }
        });
    }

    currentLegacyBoardStyle = options.boardStyle;
    currentLegacyPieceStyle = options.pieceStyle;
    currentThemeAssets = options.themeAssets;
  }
}

/**
 * Emits an error which signals fallback styles usage
 * @param context
 * @param err
 */
function emitError(context, err) {
  context.api.emit(
    Core.InstanceEvents.StylesSetFailed,
    createCoreEvent(Core.InstanceEvents.StylesSetFailed, {
      message: 'Problem updating styles. Will fallback to default.',
      error: err,
    }),
  );
}
